import { CreditCardOutlined } from "@ant-design/icons";
import { message, Select, Spin } from "antd";
import { toJalaali } from "jalaali-js";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import api from "../../../api/api";
import DataTable from "../../../Components/DataTable/DataTable";
import * as actions from "../../../store/actions/action";
import { dataFetchError } from "../../../Util/Messages";
import ConvertNumbersToEn from "../../../Util/ConvertNumbersToEn";

const CardToCards = ({ ClearLoading, LoadingSetter, permissions }) => {
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loadingData, setLoadingData] = useState(false);
  const [currentFilters, setCurrentFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(100);

  const filtersBeautify = (fData) => {
    const filters = {
      ...(fData.status?.at(0) ? { status: fData.status?.at(0) } : {}),
      ...(fData.last_4_numbers?.at(0)
        ? { last_4_numbers: ConvertNumbersToEn(fData.last_4_numbers?.at(0)) }
        : {}),
      ...(fData.firstname?.at(0) ? { firstname: fData.firstname?.at(0) } : {}),
      ...(fData.lastname?.at(0) ? { lastname: fData.lastname?.at(0) } : {}),
      ...(fData.mobile?.at(0)
        ? { mobile: ConvertNumbersToEn(fData.mobile?.at(0)) }
        : {}),
    };

    // Handle date range filters
    if (fData.payment_date_from?.at(0)) {
      const fromDate = fData.payment_date_from?.at(0);
      filters.payment_date_from =
        typeof fromDate === "string"
          ? fromDate
          : new Date(fromDate).toISOString();
    }
    if (fData.payment_date_to?.at(0)) {
      const toDate = fData.payment_date_to?.at(0);
      filters.payment_date_to =
        typeof toDate === "string" ? toDate : new Date(toDate).toISOString();
    }

    return filters;
  };

  const loadData = (page = 1, pageSize = 100, filter = {}) => {
    setCurrentPage(page);
    setCurrentPageSize(pageSize);
    setCurrentFilters(filter);
    setLoadingData(true);
    LoadingSetter();
    api
      .get("/card-to-cards", {
        params: { page, pageSize, ...filter },
      })
      .then((res) => {
        const mappedData = (res.data.results || []).map((el) => ({
          id: el.id,
          key: el.id,
          last_4_numbers: el.last_4_numbers,
          payment_date: el.payment_date,
          status: el.status,
          createdAt: el.createdAt,
          updatedAt: el.updatedAt,
          publishedAt: el.publishedAt,
          user_firstname: el.user?.firstname || "-",
          user_lastname: el.user?.lastname || "-",
          user_username: el.user?.username || "-",
          user_id: el.user?.id,
          reviewer_firstname: el.reviewer?.firstname || null,
          reviewer_lastname: el.reviewer?.lastname || null,
          reviewer_username: el.reviewer?.username || null,
          reviewer_id: el.reviewer?.id || null,
          finalOrderPrice: el.finalOrderPrice || null,
          discountCode: el.discountCode || null,
          productCode: el.productCode || null,
          productTitle: el.productTitle || null,
          roadmapKey: el.roadmapKey || null,
          roadmapId: el.roadmapId || null,
          orderId: el.order?.id || null,
        }));

        setData(mappedData);
        setPagination(res.data.pagination || {});
        ClearLoading();
      })
      .catch(() => {
        ClearLoading();
        dataFetchError();
      })
      .finally(() => setLoadingData(false));
  };

  const statusMapper = (status) => {
    const statusMap = {
      pending: "در انتظار",
      accept: "تایید شده",
      reject: "رد شده",
    };
    return statusMap[status] || status;
  };

  const changeStatusHandler = (id, newStatus) => {
    api
      .put(`/card-to-cards/${id}/status`, { status: newStatus })
      .then(() => {
        message.success("وضعیت با موفقیت تغییر کرد");
        // Reload the list with current filters and pagination
        loadData(currentPage, currentPageSize, currentFilters);
      })
      .catch(() => message.error("خطا در تغییر وضعیت"));
  };

  const columns = [
    {
      title: "۴ رقم آخر کارت",
      dataIndex: "last_4_numbers",
      width: "7%",
      searchable: true,
      render: (text) => {
        return (
          <span
            onClick={() => {
              navigator.clipboard.writeText(text);
              message.success("۴ رقم آخر کارت در کلیپ بورد کپی شد");
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: "وضعیت",
      dataIndex: "status",
      width: "10%",
      filterMultiple: false,
      filters: [
        { text: statusMapper("pending"), value: "pending" },
        { text: statusMapper("accept"), value: "accept" },
        { text: statusMapper("reject"), value: "reject" },
      ],
      render: (status, record) => {
        return (
          <Select
            style={{ width: "100%" }}
            value={status}
            onChange={(val) => changeStatusHandler(record.id, val)}
            options={[
              { label: statusMapper("pending"), value: "pending" },
              { label: statusMapper("accept"), value: "accept" },
              { label: statusMapper("reject"), value: "reject" },
            ]}
            suffixIcon={null}
          />
        );
      },
    },
    {
      title: "تاریخ پرداخت",
      dataIndex: "payment_date",
      width: "10%",
      render: (text) => {
        try {
          if (!text) return "-";
          const date = new Date(text);
          const jalali = toJalaali(
            date.getFullYear(),
            date.getMonth() + 1,
            date.getDate()
          );
          return (
            <span dir="ltr">
              {jalali.jy}-{String(jalali.jm).padStart(2, "0")}-
              {String(jalali.jd).padStart(2, "0")}{" "}
              {date.toLocaleTimeString("eo", { hour12: false })}
            </span>
          );
        } catch (error) {
          console.log("error in payment_date", { text, error });
          return "خطا";
        }
      },
    },
    {
      title: "نام کاربر",
      dataIndex: "user_firstname",
      width: "8%",
      searchable: true,
      render: (text, record) => {
        return (
          <span
            onClick={() => {
              navigator.clipboard.writeText(record?.user_firstname);
              message.success("نام در کلیپ بورد کپی شد");
            }}
          >
            {record?.user_firstname}
          </span>
        );
      },
    },
    {
      title: "نام خانوادگی کاربر",
      dataIndex: "user_lastname",
      width: "8%",
      searchable: true,
      render: (text, record) => {
        return (
          <span
            onClick={() => {
              navigator.clipboard.writeText(record?.user_lastname);
              message.success("نام خانوادگی در کلیپ بورد کپی شد");
            }}
          >
            {record?.user_lastname}
          </span>
        );
      },
    },
    {
      title: "موبایل کاربر",
      dataIndex: "user_username",
      width: "8%",
      // searchable: true,
      render: (value, record) => {
        return (
          <span
            style={{ cursor: "pointer" }}
            onClick={() => {
              navigator.clipboard.writeText(value);
              message.success("شماره موبایل با موفقیت کپی شد");
            }}
          >
            {value}
          </span>
        );
      },
    },
    {
      title: "بررسی کننده",
      dataIndex: "reviewer_firstname",
      width: "10%",
      render: (text, record) => {
        if (record.reviewer_firstname && record.reviewer_lastname) {
          return `${record.reviewer_firstname} ${record.reviewer_lastname}`;
        }
        return "-";
      },
    },
    {
      title: "محصول",
      dataIndex: "productTitle",
      width: "10%",
      align: "center",
      render: (text, record) => {
        const title = record.productTitle || record.productCode || "-";
        return (
          <span
            style={{ cursor: record.productCode ? "pointer" : "default" }}
            onClick={() => {
              if (record.productCode) {
                navigator.clipboard.writeText(record.productCode);
                message.success("کد محصول کپی شد");
              }
            }}
          >
            {title}
          </span>
        );
      },
    },
    {
      title: "کلید نقشه",
      dataIndex: "roadmapKey",
      width: "10%",
      align: "center",
      render: (text, record) => {
        if (!record.roadmapKey) return "-";
        const short = record.roadmapKey.slice(0, 8);
        return (
          <span
            style={{ cursor: "pointer", direction: "ltr" }}
            onClick={() => {
              navigator.clipboard.writeText(record.roadmapKey);
              message.success("کلید نقشه کپی شد");
            }}
          >
            {short}…
          </span>
        );
      },
    },
    {
      title: "شناسه سفارش",
      dataIndex: "orderId",
      width: "7%",
      align: "center",
      render: (text) => text || "-",
    },
    {
      title: "مبلغ نهایی",
      dataIndex: "finalOrderPrice",
      width: "8%",
      align: "center",
      render: (text) => {
        if (!text) return "-";
        const price = typeof text === "string" ? parseFloat(text) : text;
        if (isNaN(price)) return "-";
        return `${price.toLocaleString("fa-IR")} تومان`;
      },
    },
    {
      title: "کد تخفیف",
      dataIndex: "discountCode",
      width: "8%",
      align: "center",
      render: (text) => {
        return text || "-";
      },
    },
    {
      title: "تاریخ ایجاد",
      dataIndex: "createdAt",
      width: "10%",
      render: (text) => {
        try {
          if (!text) return "-";
          const date = new Date(text);
          const jalali = toJalaali(
            date.getFullYear(),
            date.getMonth() + 1,
            date.getDate()
          );
          return (
            <span dir="ltr">
              {jalali.jy}-{String(jalali.jm).padStart(2, "0")}-
              {String(jalali.jd).padStart(2, "0")}{" "}
              {date.toLocaleTimeString("eo", { hour12: false })}
            </span>
          );
        } catch (error) {
          console.log("error in createdAt", { text, error });
          return "خطا";
        }
      },
    },
    {
      title: "تاریخ بروزرسانی",
      dataIndex: "updatedAt",
      width: "10%",
      render: (text) => {
        try {
          if (!text) return "-";
          const date = new Date(text);
          const jalali = toJalaali(
            date.getFullYear(),
            date.getMonth() + 1,
            date.getDate()
          );
          return (
            <span dir="ltr">
              {jalali.jy}-{String(jalali.jm).padStart(2, "0")}-
              {String(jalali.jd).padStart(2, "0")}{" "}
              {date.toLocaleTimeString("eo", { hour12: false })}
            </span>
          );
        } catch (error) {
          console.log("error in updatedAt", { text, error });
          return "خطا";
        }
      },
    },
  ];

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <DataTable
        tableKey="card-to-cards"
        hideErrorReport
        hideComment
        filtersBeautify={filtersBeautify}
        dynamicPagination
        recordsCount={pagination.total}
        header={{
          title: (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "1rem",
                width: "100%",
              }}
            >
              <span style={{ display: "flex", alignItems: "center" }}>
                <p>کارت به کارت</p>
              </span>
              {loadingData && <Spin size="small" />}
            </div>
          ),
          avatar: { icon: <CreditCardOutlined />, size: "large" },
        }}
        columns={columns}
        dataSource={data}
        setDataSource={setData}
        reload={loadData}
        recordCreatorDisabled
      />
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    loading: state.loading,
    error: state.error,
    permissions: state.userData ? state.userData.permissions : null,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    ClearError: () => dispatch(actions.ClearError()),
    ClearLoading: () => dispatch(actions.ClearLoading()),
    LoadingSetter: () => dispatch(actions.LoadingSetter()),
    ErrorSetter: () => dispatch(actions.ErrorSetter()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(CardToCards);
